﻿import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Heading,
  Text,
  Input,
  Button,
  LoadingSpinner,
  Select,
  SortableTableHeader,
} from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { dashcorretorService } from '../../services/dashcorretor'
import { comercialService } from '../../services/comercial'
import { comercialGestaoService } from '../../services/comercialGestao'
import { shopService } from '../../services/shop'
import type { ComercialCupom, ComercialUser } from '../../types/comercial'
import type { ShopBroker } from '../../types/shop'
import type {
  DashCorretorBateuLevouEntry,
  DashCorretorCampeaoMesEntry,
  DashCorretorDecoleInfluenciadoresEntry,
  DashCorretorMinimetas2026Entry,
  DashCorretorReiImediatosEntry,
  DashCorretorClassificationRef,
  DashCorretorClassificationUpgradeEntry,
  DashCorretorIndicationsSummaryEntry,
} from '../../types/dashcorretor'
import { ClassificationLabel } from '../../components/ClassificationTrendIndicator'
import { ClassificationKpiCards } from '../../components/ClassificationKpiCards'
import {
  buildClassificationColorMap,
  CLASSIFICATION_UPGRADE_GAP_FILTERS,
  CLASSIFICATION_UPGRADE_NEAR_GAP,
  formatClassificationUpgradeHint,
  normalizeClassificationKey,
  resolveClassificationProgress,
} from '../../utils/classificationTrend'
import { formatCurrencyBRL } from '../../utils/cotacaoFormatters'
import {
  TrophyIcon,
  StarIcon,
  RocketLaunchIcon,
  BoltIcon,
  FireIcon,
  SparklesIcon,
  ClockIcon,
  FunnelIcon,
  CheckCircleIcon,
  XMarkIcon,
  UserPlusIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts'

type ActiveTab =
  | 'minimetas2026'
  | 'upgradeCategoria'
  | 'campeaoMes'
  | 'decoleInfluenciadores'
  | 'reiImediatos'
  | 'bateuLevou'
  | 'indicacoes'

type IndicacoesSubTab = 'pasiSuperApp' | 'pasiFarma'

const MINIMETAS_QUASE_LA_MARGIN = 5
const MINIMETAS_MILESTONES = [10, 20, 30, 50, 100] as const
const MINIMETAS_CHART_COLORS = ['#06b6d4', '#008B95', '#6366f1', '#8b5cf6', '#e11d48']
const MINIMETAS_MILESTONE_GRADIENTS: Record<(typeof MINIMETAS_MILESTONES)[number], string> = {
  10: 'from-cyan-500 to-primary-600',
  20: 'from-primary-500 to-indigo-600',
  30: 'from-indigo-500 to-violet-600',
  50: 'from-violet-500 to-purple-600',
  100: 'from-fuchsia-500 to-rose-600',
}
const REI_IMEDIATOS_MILESTONES = [500, 1000] as const
const REI_QUASE_LA_MARGIN = 50
const REI_IMEDIATOS_MILESTONE_GRADIENTS: Record<(typeof REI_IMEDIATOS_MILESTONES)[number], string> = {
  500: 'from-primary-500 to-indigo-600',
  1000: 'from-emerald-500 to-emerald-600',
}
const BATEU_LEVOU_GOAL = 3000
const BATEU_LEVOU_QUASE_LA_MARGIN = 500
const CAMPEAO_MES_START = '2026-03'
const ITEMS_PER_PAGE = 20
const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function campaignRowMatchesFilters(
  item: {
    broker_id: string
    broker_code?: number | null
    broker_name: string
    classification?: string | null
  },
  codeFilter: string,
  nameFilter: string,
  classificationFilter: string,
  comercialFilter: string,
  comercialNameByBrokerId: Map<string, string>
): boolean {
  const code = codeFilter.trim()
  if (code && !String(item.broker_code ?? '').includes(code)) return false
  const name = nameFilter.trim().toLowerCase()
  if (name && !(item.broker_name || '').toLowerCase().includes(name)) return false
  const cls = normalizeClassificationKey(classificationFilter)
  if (cls && normalizeClassificationKey(item.classification) !== cls) return false
  const comercial = comercialFilter.trim()
  if (comercial) {
    const assigned = comercialNameByBrokerId.get(item.broker_id) || ''
    if (assigned !== comercial) return false
  }
  return true
}

function formatReferenceMonth(referenceMonth: string): string {
  const key = normalizeReferenceMonthKey(referenceMonth)
  const [year, month] = key.split('-')
  if (year && month) {
    return `${month}/${year}`
  }
  return referenceMonth
}

function normalizeReferenceMonthKey(referenceMonth: string): string {
  const trimmed = (referenceMonth ?? '').trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})/)
  if (match) return `${match[1]}-${match[2]}`
  return trimmed
}

function truncateName(name: string, max = 40): string {
  if (!name) return '—'
  return name.length > max ? `${name.slice(0, max)}…` : name
}

function getAchievedMinimetasMilestones(qty: number): number[] {
  return MINIMETAS_MILESTONES.filter((m) => qty >= m)
}

function getNextMinimetasMilestone(qty: number): number | null {
  return MINIMETAS_MILESTONES.find((m) => qty < m) ?? null
}

function isAlmostAtMinimetasMilestone(qty: number, milestone: number): boolean {
  return qty < milestone && qty >= milestone - MINIMETAS_QUASE_LA_MARGIN
}

function getAchievedReiMilestones(qty: number): number[] {
  return REI_IMEDIATOS_MILESTONES.filter((m) => qty >= m)
}

function getNextReiMilestone(qty: number): number | null {
  return REI_IMEDIATOS_MILESTONES.find((m) => qty < m) ?? null
}

function isAlmostAtReiMilestone(qty: number, milestone: number): boolean {
  return qty < milestone && qty >= milestone - REI_QUASE_LA_MARGIN
}

type ReiMarcoFilter = 'all' | (typeof REI_IMEDIATOS_MILESTONES)[number] | 'quase_la'

function matchesReiMarcoFilter(quantidade: number, filter: ReiMarcoFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'quase_la') {
    const next = getNextReiMilestone(quantidade)
    return next !== null && isAlmostAtReiMilestone(quantidade, next)
  }
  return quantidade >= filter
}

type BateuLevouMarcoFilter = 'all' | 'meta' | 'quase_la'

function isAlmostAtBateuLevouGoal(paidPremium: number): boolean {
  return paidPremium < BATEU_LEVOU_GOAL && paidPremium >= BATEU_LEVOU_GOAL - BATEU_LEVOU_QUASE_LA_MARGIN
}

function matchesBateuLevouMarcoFilter(paidPremium: number, filter: BateuLevouMarcoFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'meta') return paidPremium >= BATEU_LEVOU_GOAL
  if (filter === 'quase_la') return isAlmostAtBateuLevouGoal(paidPremium)
  return true
}

type MinimetasMarcoFilter = 'all' | (typeof MINIMETAS_MILESTONES)[number] | 'quase_la'

function matchesMinimetasMarcoFilter(quantidade: number, filter: MinimetasMarcoFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'quase_la') {
    const next = getNextMinimetasMilestone(quantidade)
    return next !== null && isAlmostAtMinimetasMilestone(quantidade, next)
  }
  return quantidade >= filter
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  })
}

function formatCupomValue(cupom: Pick<ComercialCupom, 'discount_type' | 'value'>): string {
  return cupom.discount_type === 'percent'
    ? `${Number(cupom.value)}%`
    : formatCurrency(Number(cupom.value))
}

interface BrokerCupomGroup {
  key: string
  code: string
  discount_type: ComercialCupom['discount_type']
  value: number
  valid_until: string | null
  availableCount: number
  usedCount: number
}

function cupomQuantityLabel(count: number, singular: string, plural: string): string {
  return `${count.toLocaleString('pt-BR')} ${count === 1 ? singular : plural}`
}

function groupBrokerCupons(cupons: ComercialCupom[]): BrokerCupomGroup[] {
  const groups = new Map<string, BrokerCupomGroup>()

  for (const cupom of cupons) {
    const validKey = cupom.valid_until ? cupom.valid_until.slice(0, 10) : ''
    const key = `${cupom.code}|${cupom.discount_type}|${Number(cupom.value)}|${validKey}`
    const current = groups.get(key) ?? {
      key,
      code: cupom.code,
      discount_type: cupom.discount_type,
      value: Number(cupom.value),
      valid_until: cupom.valid_until,
      availableCount: 0,
      usedCount: 0,
    }

    if (cupom.used_at) current.usedCount += 1
    else current.availableCount += 1

    groups.set(key, current)
  }

  return Array.from(groups.values()).sort((a, b) => {
    const aUsedOnly = a.availableCount === 0 ? 1 : 0
    const bUsedOnly = b.availableCount === 0 ? 1 : 0
    if (aUsedOnly !== bUsedOnly) return aUsedOnly - bUsedOnly

    const byDate = (a.valid_until ?? '').localeCompare(b.valid_until ?? '')
    if (byDate !== 0) return byDate

    return a.code.localeCompare(b.code, 'pt-BR', { sensitivity: 'base' })
  })
}

function formatCupomDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

type SortOrder = 'asc' | 'desc'

function compareText(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', 'pt-BR', { sensitivity: 'base' })
}

function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  emptyLast = true
): number {
  const na = a ?? (emptyLast ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY)
  const nb = b ?? (emptyLast ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY)
  return na - nb
}

function applySortOrder(result: number, sortOrder: SortOrder): number {
  return sortOrder === 'asc' ? result : -result
}

function sortMinimetasItems(
  items: DashCorretorMinimetas2026Entry[],
  sortBy: string,
  sortOrder: SortOrder
): DashCorretorMinimetas2026Entry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'quantidade':
        res = a.quantidade - b.quantidade
        break
      case 'marcos':
        res =
          getAchievedMinimetasMilestones(a.quantidade).length -
          getAchievedMinimetasMilestones(b.quantidade).length
        break
      case 'proxima_meta': {
        const nextA = getNextMinimetasMilestone(a.quantidade)
        const nextB = getNextMinimetasMilestone(b.quantidade)
        const gapA = nextA === null ? Number.POSITIVE_INFINITY : nextA - a.quantidade
        const gapB = nextB === null ? Number.POSITIVE_INFINITY : nextB - b.quantidade
        res = gapA - gapB
        break
      }
      default:
        res = b.quantidade - a.quantidade
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return compareText(a.broker_name, b.broker_name)
  })
}

function sortUpgradeItems(
  items: DashCorretorClassificationUpgradeEntry[],
  sortBy: string,
  sortOrder: SortOrder
): DashCorretorClassificationUpgradeEntry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'next_classification':
        res = compareText(a.next_classification_name, b.next_classification_name)
        break
      case 'gap':
        res = a.gap_to_next_classification - b.gap_to_next_classification
        break
      case 'premium':
        res = a.monthly_paid_premium - b.monthly_paid_premium
        break
      default:
        res = a.gap_to_next_classification - b.gap_to_next_classification
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return compareText(a.broker_name, b.broker_name)
  })
}

function sortDecoleItems(
  items: DashCorretorDecoleInfluenciadoresEntry[],
  sortBy: string,
  sortOrder: SortOrder,
  rankById: Map<string, number>
): DashCorretorDecoleInfluenciadoresEntry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'posicao':
        res = (rankById.get(a.id) ?? 0) - (rankById.get(b.id) ?? 0)
        break
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'value':
        res = a.value - b.value
        break
      default:
        res = (rankById.get(a.id) ?? 0) - (rankById.get(b.id) ?? 0)
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return (rankById.get(a.id) ?? 0) - (rankById.get(b.id) ?? 0)
  })
}

function sortReiItems(
  items: DashCorretorReiImediatosEntry[],
  sortBy: string,
  sortOrder: SortOrder
): DashCorretorReiImediatosEntry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'quantidade':
        res = a.quantidade - b.quantidade
        break
      case 'progresso':
        res = a.quantidade - b.quantidade
        break
      case 'premiacao':
        res =
          getAchievedReiMilestones(a.quantidade).length -
          getAchievedReiMilestones(b.quantidade).length
        break
      case 'proximo_marco': {
        const nextA = getNextReiMilestone(a.quantidade)
        const nextB = getNextReiMilestone(b.quantidade)
        const gapA = nextA === null ? Number.POSITIVE_INFINITY : nextA - a.quantidade
        const gapB = nextB === null ? Number.POSITIVE_INFINITY : nextB - b.quantidade
        res = gapA - gapB
        break
      }
      default:
        res = b.quantidade - a.quantidade
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return compareText(a.broker_name, b.broker_name)
  })
}

function sortPasiIndicationsItems(
  items: DashCorretorIndicationsSummaryEntry[],
  sortBy: string,
  sortOrder: SortOrder
): DashCorretorIndicationsSummaryEntry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'quantidade_ativas':
        res = a.quantidade_ativas - b.quantidade_ativas
        break
      case 'quantidade_inativas':
        res = a.quantidade_inativas - b.quantidade_inativas
        break
      case 'quantidade_total':
        res = a.quantidade_total - b.quantidade_total
        break
      default:
        res = b.quantidade_ativas - a.quantidade_ativas
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return compareText(a.broker_name, b.broker_name)
  })
}

function sortBateuLevouItems(
  items: DashCorretorBateuLevouEntry[],
  sortBy: string,
  sortOrder: SortOrder
): DashCorretorBateuLevouEntry[] {
  return [...items].sort((a, b) => {
    let res = 0
    switch (sortBy) {
      case 'broker_code':
        res = compareNullableNumber(a.broker_code, b.broker_code)
        break
      case 'broker_name':
        res = compareText(a.broker_name, b.broker_name)
        break
      case 'state':
        res = compareText(a.state?.trim(), b.state?.trim())
        break
      case 'classification':
        res = compareText(a.classification, b.classification)
        break
      case 'progresso':
        res = a.paid_premium - b.paid_premium
        break
      case 'paid_premium':
        res = a.paid_premium - b.paid_premium
        break
      case 'pending_premium':
        res = a.pending_premium - b.pending_premium
        break
      case 'total_projetado':
        res = a.paid_premium + a.pending_premium - (b.paid_premium + b.pending_premium)
        break
      default:
        res = b.paid_premium - a.paid_premium
    }
    if (res !== 0) return applySortOrder(res, sortOrder)
    return compareText(a.broker_name, b.broker_name)
  })
}

function createToggleSort(
  sortBy: string,
  setSortBy: React.Dispatch<React.SetStateAction<string>>,
  sortOrder: SortOrder,
  setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>,
  resetPage: () => void
) {
  return (key: string) => {
    if (sortBy === key) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
    resetPage()
  }
}

type CampaignBrokerBase = Pick<
  DashCorretorMinimetas2026Entry,
  'broker_id' | 'broker_code' | 'broker_name' | 'classification' | 'state'
>

function getBrokerRowHandlers(brokerId: string, onOpen: (id: string) => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    title: 'Ver detalhes do corretor',
    onClick: () => onOpen(brokerId),
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpen(brokerId)
      }
    },
  }
}

function getBrokerCardHandlers(brokerId: string, onOpen: (id: string) => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    title: 'Ver detalhes do corretor',
    onClick: () => onOpen(brokerId),
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpen(brokerId)
      }
    },
  }
}

function pickBrokerBase(...sources: (CampaignBrokerBase | null | undefined)[]): CampaignBrokerBase | null {
  for (const source of sources) {
    if (source?.broker_id) return source
  }
  return null
}

function findDecoleRank(
  items: DashCorretorDecoleInfluenciadoresEntry[],
  brokerId: string
): { item: DashCorretorDecoleInfluenciadoresEntry; position: number } | null {
  const index = items.findIndex((item) => item.broker_id === brokerId)
  if (index === -1) return null
  return { item: items[index], position: index + 1 }
}

interface BrokerDetailModalProps {
  loading: boolean
  classificationColorMap: ReturnType<typeof buildClassificationColorMap>
  classificationRefs: DashCorretorClassificationRef[]
  monthlyPaidPremium: number | null
  loadingMonthlyPremium: boolean
  onClose: () => void
  brokerInfo: CampaignBrokerBase | null
  comercialResponsibleName: string | null
  loadingComercialName: boolean
  brokerPoints: ShopBroker | null
  loadingBrokerPoints: boolean
  brokerCupons: ComercialCupom[]
  loadingBrokerCupons: boolean
  minimetas?: DashCorretorMinimetas2026Entry
  campeaoMes: DashCorretorCampeaoMesEntry[]
  decoleVendas: { item: DashCorretorDecoleInfluenciadoresEntry; position: number } | null
  decoleProducao: { item: DashCorretorDecoleInfluenciadoresEntry; position: number } | null
  rei?: DashCorretorReiImediatosEntry
  bateuLevouJun?: DashCorretorBateuLevouEntry
  bateuLevouOut?: DashCorretorBateuLevouEntry
  pasiApp?: DashCorretorIndicationsSummaryEntry | null
  pasiFarma?: DashCorretorIndicationsSummaryEntry | null
  loadingPasiIndications?: boolean
}

const ModalSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <Text size="sm" className="font-semibold text-gray-900 dark:text-white mb-2">
      {title}
    </Text>
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">{children}</div>
  </div>
)

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <Text size="xs" color="muted">
      {label}
    </Text>
    <div className="text-gray-900 dark:text-white font-medium mt-0.5">{value}</div>
  </div>
)

const BrokerDetailModal: React.FC<BrokerDetailModalProps> = ({
  loading,
  classificationColorMap,
  classificationRefs,
  monthlyPaidPremium,
  loadingMonthlyPremium,
  onClose,
  brokerInfo,
  comercialResponsibleName,
  loadingComercialName,
  brokerPoints,
  loadingBrokerPoints,
  brokerCupons,
  loadingBrokerCupons,
  minimetas,
  campeaoMes,
  decoleVendas,
  decoleProducao,
  rei,
  bateuLevouJun,
  bateuLevouOut,
  pasiApp,
  pasiFarma,
  loadingPasiIndications,
}) => {
  const groupedCupons = useMemo(() => groupBrokerCupons(brokerCupons), [brokerCupons])
  const { hasPermission: canViewDecoleValues, loading: loadingDecolePermission } = usePermission(
    'comercial_campanhas2026',
    'view_values'
  )
  const minimetasAchieved = minimetas ? getAchievedMinimetasMilestones(minimetas.quantidade) : []
  const minimetasNext = minimetas ? getNextMinimetasMilestone(minimetas.quantidade) : null
  const reiAchieved = rei ? getAchievedReiMilestones(rei.quantidade) : []
  const reiNext = rei ? getNextReiMilestone(rei.quantidade) : null

  const classificationProgress =
    !loadingMonthlyPremium && monthlyPaidPremium != null && classificationRefs.length > 0
      ? resolveClassificationProgress(
          monthlyPaidPremium,
          classificationRefs.map((c) => ({ name: c.name, min_value: c.min_value }))
        )
      : null
  const upgradeHint = classificationProgress
    ? formatClassificationUpgradeHint(
        classificationProgress,
        formatCurrencyBRL,
        brokerInfo?.classification
      )
    : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="broker-detail-title"
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Heading as="h2" size="2xl" id="broker-detail-title" className="text-gray-900 dark:text-white truncate">
              {brokerInfo?.broker_name ?? 'Corretor'}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Detalhes nas campanhas 2026
            </Text>
          </div>
          <Button variant="ghost" type="button" onClick={onClose} aria-label="Fechar">
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && !brokerInfo ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <ModalSection title="Identificação">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DetailItem label="Nome" value={brokerInfo?.broker_name ?? '—'} />
                  <DetailItem label="Código SCGP" value={brokerInfo?.broker_code ?? '—'} />
                  <DetailItem
                    label="Comercial responsável"
                    value={loadingComercialName ? 'Carregando…' : comercialResponsibleName ?? '—'}
                  />
                  <DetailItem label="UF" value={brokerInfo?.state?.trim() || '—'} />
                  <DetailItem
                    label="Categoria"
                    value={
                      <div className="flex flex-col gap-0.5">
                        {brokerInfo?.classification ? (
                          <ClassificationLabel classification={brokerInfo.classification} colorMap={classificationColorMap} />
                        ) : (
                          '—'
                        )}
                        {loadingMonthlyPremium ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">Carregando…</span>
                        ) : upgradeHint ? (
                          <span
                            className={`text-xs font-normal ${
                              classificationProgress?.is_near_upgrade
                                ? 'text-amber-700 dark:text-amber-300 font-medium'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {upgradeHint}
                          </span>
                        ) : null}
                      </div>
                    }
                  />
                </div>
              </ModalSection>

              <ModalSection title="Minimetas 2026">
                {minimetas ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <DetailItem label="Propostas" value={minimetas.quantidade.toLocaleString('pt-BR')} />
                      <DetailItem
                        label="Próxima meta"
                        value={
                          minimetasNext
                            ? `Faltam ${minimetasNext - minimetas.quantidade} para ${minimetasNext}`
                            : 'Todas as metas atingidas ✓'
                        }
                      />
                    </div>
                    <div>
                      <Text size="xs" color="muted" className="mb-1">
                        Marcos
                      </Text>
                      <div className="flex flex-wrap gap-1">
                        {MINIMETAS_MILESTONES.map((m) => (
                          <span
                            key={m}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              minimetasAchieved.includes(m)
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                            }`}
                          >
                            {m} {minimetasAchieved.includes(m) ? '✓' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Text size="sm" color="muted">
                    Sem registro nesta campanha.
                  </Text>
                )}
              </ModalSection>

              <ModalSection title="Campeão do Mês">
                {campeaoMes.length > 0 ? (
                  <div className="space-y-2">
                    {campeaoMes
                      .slice()
                      .sort((a, b) => normalizeReferenceMonthKey(a.reference_month).localeCompare(normalizeReferenceMonthKey(b.reference_month)))
                      .map((entry) => (
                        <div
                          key={`${entry.reference_month}-${entry.campaign_id}`}
                          className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-200 dark:border-gray-700 last:border-0 pb-2 last:pb-0"
                        >
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatReferenceMonth(entry.reference_month)}
                          </span>
                          {entry.value > 0 && (
                            <span className="text-amber-700 dark:text-amber-300 font-semibold">
                              {formatCurrency(entry.value)}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <Text size="sm" color="muted">
                    Não foi campeão em nenhum mês registrado.
                  </Text>
                )}
              </ModalSection>

              <ModalSection title="Decole com Influenciadores">
                {loadingDecolePermission ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Vendas
                      </Text>
                      {decoleVendas ? (
                        <div className="space-y-2">
                          <DetailItem label="Posição" value={`${decoleVendas.position}°`} />
                          {canViewDecoleValues && (
                            <DetailItem label="Valor" value={formatCurrency(decoleVendas.item.value)} />
                          )}
                        </div>
                      ) : (
                        <Text size="sm" color="muted">
                          Sem registro.
                        </Text>
                      )}
                    </div>
                    <div>
                      <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        Produção
                      </Text>
                      {decoleProducao ? (
                        <div className="space-y-2">
                          <DetailItem label="Posição" value={`${decoleProducao.position}°`} />
                          {canViewDecoleValues && (
                            <DetailItem label="Valor" value={formatCurrency(decoleProducao.item.value)} />
                          )}
                        </div>
                      ) : (
                        <Text size="sm" color="muted">
                          Sem registro.
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </ModalSection>

              <ModalSection title="Bateu, Levou! Imediatos">
                {rei ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <DetailItem label="Imediatos" value={rei.quantidade.toLocaleString('pt-BR')} />
                      <DetailItem
                        label="Próximo marco"
                        value={
                          reiNext
                            ? `Faltam ${reiNext - rei.quantidade} para ${reiNext}`
                            : 'Meta máxima atingida ✓'
                        }
                      />
                    </div>
                    <div>
                      <Text size="xs" color="muted" className="mb-1">
                        Premiação
                      </Text>
                      <div className="flex flex-wrap gap-1">
                        {REI_IMEDIATOS_MILESTONES.map((m) => (
                          <span
                            key={m}
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              reiAchieved.includes(m)
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                            }`}
                          >
                            {m} {reiAchieved.includes(m) ? '✓' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Text size="sm" color="muted">
                    Sem registro nesta campanha.
                  </Text>
                )}
              </ModalSection>

              <ModalSection title="Bateu Levou">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Período Junho', data: bateuLevouJun },
                    { label: 'Período Outubro', data: bateuLevouOut },
                  ].map(({ label, data }) => (
                    <div key={label}>
                      <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                        {label}
                      </Text>
                      {data ? (
                        <div className="space-y-2">
                          <DetailItem label="Prêmio pago" value={formatCurrency(data.paid_premium)} />
                          <DetailItem label="Pendente" value={formatCurrency(data.pending_premium)} />
                          <DetailItem
                            label="Total projetado"
                            value={formatCurrency(data.paid_premium + data.pending_premium)}
                          />
                          <DetailItem
                            label="Meta R$ 3.000"
                            value={
                              data.paid_premium >= BATEU_LEVOU_GOAL
                                ? 'Bateu a meta ✓'
                                : isAlmostAtBateuLevouGoal(data.paid_premium)
                                  ? `Quase lá - faltam ${formatCurrency(BATEU_LEVOU_GOAL - data.paid_premium)}`
                                  : `Faltam ${formatCurrency(Math.max(0, BATEU_LEVOU_GOAL - data.paid_premium))}`
                            }
                          />
                        </div>
                      ) : (
                        <Text size="sm" color="muted">
                          Sem registro.
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              </ModalSection>

              <ModalSection title="Indicações">
                {loadingPasiIndications ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: 'PASI Super APP', data: pasiApp },
                      { label: 'PASI Farma', data: pasiFarma },
                    ].map(({ label, data }) => (
                      <div key={label}>
                        <Text size="xs" className="font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                          {label}
                        </Text>
                        {data ? (
                          <div className="space-y-2">
                            <DetailItem label="Ativas" value={data.quantidade_ativas.toLocaleString('pt-BR')} />
                            <DetailItem label="Inativas" value={data.quantidade_inativas.toLocaleString('pt-BR')} />
                            <DetailItem label="Total" value={data.quantidade_total.toLocaleString('pt-BR')} />
                          </div>
                        ) : (
                          <Text size="sm" color="muted">
                            Sem registro.
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ModalSection>

              <ModalSection title="Pontuação">
                {loadingBrokerPoints ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : brokerPoints ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <DetailItem
                      label="Saldo atual"
                      value={
                        <span
                          className={
                            brokerPoints.points_balance >= 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                          }
                        >
                          {brokerPoints.points_balance.toLocaleString('pt-BR')} PASI Points
                        </span>
                      }
                    />
                    <DetailItem
                      label="PASI Points ganhos"
                      value={brokerPoints.total_points_earned.toLocaleString('pt-BR')}
                    />
                    <DetailItem
                      label="PASI Points gastos"
                      value={brokerPoints.total_points_spent.toLocaleString('pt-BR')}
                    />
                  </div>
                ) : (
                  <Text size="sm" color="muted">
                    Corretor sem cadastro no Shop - pontuação indisponível.
                  </Text>
                )}
              </ModalSection>

              <ModalSection title="Cupons">
                {loadingBrokerCupons ? (
                  <div className="flex items-center justify-center py-6">
                    <LoadingSpinner />
                  </div>
                ) : groupedCupons.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum cupom para este corretor.
                  </Text>
                ) : (
                  <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                    {groupedCupons.map((group) => (
                      <li
                        key={group.key}
                        className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-white">{group.code}</span>
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              {cupomQuantityLabel(group.availableCount, 'disponível', 'disponíveis')}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                              {cupomQuantityLabel(group.usedCount, 'utilizado', 'utilizados')}
                            </span>
                          </div>
                          <Text size="xs" color="muted" className="mt-0.5">
                            {group.discount_type === 'percent' ? 'Percentual' : 'Valor fixo'} · Validade:{' '}
                            {formatCupomDate(group.valid_until)}
                          </Text>
                        </div>
                        <span className="font-medium text-emerald-700 dark:text-emerald-300 shrink-0">
                          {formatCupomValue(group)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </ModalSection>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <Button variant="outline" type="button" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

const FILTER_FIELD_LABEL_CLASS = 'text-gray-700 dark:text-gray-300 mb-2 block'

interface CampaignSharedFiltersProps {
  codeFilter: string
  nameFilter: string
  classificationFilter: string
  comercialFilter: string
  classificationOptions: string[]
  comercialUsers: ComercialUser[]
  loadingComercialUsers: boolean
  comercialFilterLocked?: boolean
  embedded?: boolean
  onCodeChange: (value: string) => void
  onNameChange: (value: string) => void
  onClassificationChange: (value: string) => void
  onComercialChange: (value: string) => void
  onClear: () => void
}

const CampaignSharedFilters: React.FC<CampaignSharedFiltersProps> = ({
  codeFilter,
  nameFilter,
  classificationFilter,
  comercialFilter,
  classificationOptions,
  comercialUsers,
  loadingComercialUsers,
  comercialFilterLocked = false,
  embedded = false,
  onCodeChange,
  onNameChange,
  onClassificationChange,
  onComercialChange,
  onClear,
}) => (
  <div
    className={
      embedded
        ? 'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4'
        : 'px-6 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50'
    }
  >
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      <div>
        <Text size="sm" className={FILTER_FIELD_LABEL_CLASS}>
          Código do corretor
        </Text>
        <Input
          type="text"
          placeholder="Filtrar por código..."
          value={codeFilter}
          onChange={(e) => onCodeChange(e.target.value)}
          className="w-full"
        />
      </div>
      <div>
        <Text size="sm" className={FILTER_FIELD_LABEL_CLASS}>
          Nome do corretor
        </Text>
        <Input
          type="text"
          placeholder="Filtrar por nome..."
          value={nameFilter}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full"
        />
      </div>
      <div>
        <Text size="sm" className={FILTER_FIELD_LABEL_CLASS}>
          Categoria
        </Text>
        <Select
          value={classificationFilter}
          onChange={(e) => onClassificationChange(e.target.value)}
          className="w-full"
        >
          <option value="">Todas</option>
          {classificationOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Text size="sm" className={FILTER_FIELD_LABEL_CLASS}>
          Comercial responsável
        </Text>
        <Select
          value={comercialFilter}
          onChange={(e) => onComercialChange(e.target.value)}
          className="w-full"
          disabled={loadingComercialUsers || comercialFilterLocked}
        >
          {!comercialFilterLocked && <option value="">Todos</option>}
          {comercialUsers.map((user) => (
            <option key={user.id} value={user.name}>
              {user.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex items-end">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="w-full md:w-auto"
          onClick={onClear}
          disabled={
            !codeFilter.trim() &&
            !nameFilter.trim() &&
            !classificationFilter.trim() &&
            (!comercialFilter.trim() || comercialFilterLocked)
          }
        >
          Limpar filtros
        </Button>
      </div>
    </div>
  </div>
)

interface TabFiltersPanelProps {
  title?: string
  description?: string
  showClear?: boolean
  onClear?: () => void
  headerExtra?: React.ReactNode
  children: React.ReactNode
}

const TabFiltersPanel: React.FC<TabFiltersPanelProps> = ({
  title = 'Filtros',
  description,
  showClear,
  onClear,
  headerExtra,
  children,
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
    <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
      <div>
        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
          {title}
        </Heading>
        {description && (
          <Text size="sm" color="muted" className="mt-1">
            {description}
          </Text>
        )}
      </div>
      {(headerExtra || (showClear && onClear)) && (
        <div className="flex flex-wrap items-end gap-2">
          {headerExtra}
          {showClear && onClear && (
            <Button variant="outline" size="sm" type="button" onClick={onClear}>
              Limpar filtro
            </Button>
          )}
        </div>
      )}
    </div>
    {children}
  </div>
)

interface KpiCardProps {
  label: string
  value: string | number
  gradient: string
  icon?: React.ReactNode
  onClick?: () => void
  isActive?: boolean
  compact?: boolean
  filterable?: boolean
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, gradient, icon, onClick, isActive, compact, filterable }) => {
  const Tag = onClick ? 'button' : 'div'
  const isFilter = filterable && onClick

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={isFilter ? isActive : undefined}
      title={isFilter ? (isActive ? 'Clique para remover o filtro' : 'Clique para filtrar por este marco') : undefined}
      className={`relative overflow-hidden bg-gradient-to-br ${gradient} ${compact ? 'shadow-md rounded-md py-2 px-3' : 'shadow-lg rounded-lg p-4'} text-white w-full text-left transition-all ${
        onClick ? 'cursor-pointer hover:shadow-xl' : ''
      } ${
        isFilter
          ? isActive
            ? 'ring-2 ring-white shadow-xl scale-[1.03] brightness-110'
            : 'ring-1 ring-white/30 border border-dashed border-white/40 opacity-90 hover:opacity-100 hover:ring-2 hover:ring-white/60 hover:scale-[1.01]'
          : isActive
            ? `${compact ? 'ring-2' : 'ring-4'} ring-white dark:ring-gray-300 ring-offset-2 ring-offset-gray-100 dark:ring-offset-gray-900 scale-[1.02]`
            : onClick
              ? 'hover:scale-[1.02]'
              : ''
      }`}
    >
      {isFilter && isActive && (
        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 rounded-full bg-white text-gray-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm">
          <CheckCircleIcon className="h-3 w-3 text-emerald-600" aria-hidden="true" />
          Ativo
        </span>
      )}
      {isFilter && !isActive && (
        <span className="absolute top-1.5 right-1.5 rounded-full bg-black/15 p-0.5" aria-hidden="true">
          <FunnelIcon className="h-3 w-3 text-white/80" />
        </span>
      )}
      <div className={`flex items-center justify-between ${compact ? 'gap-2' : ''}`}>
        <div className="text-center flex-1 min-w-0">
          <Text size={compact ? 'lg' : '2xl'} className={`text-white font-bold ${compact ? 'mb-0.5 leading-tight' : 'mb-1'}`}>
            {value}
          </Text>
          <Text size={compact ? 'xs' : 'sm'} className="text-white/90 leading-snug">
            {label}
          </Text>
          {isFilter && !isActive && (
            <Text size="xs" className="mt-1 text-white/70">
              Clique para filtrar
            </Text>
          )}
        </div>
        {icon && <div className={`opacity-80 shrink-0 ${compact ? '' : 'ml-2'}`}>{icon}</div>}
      </div>
    </Tag>
  )
}

interface ProgressBarProps {
  value: number
  max: number
  milestones?: number[]
  showLabels?: boolean
}

const ProgressBar: React.FC<ProgressBarProps> = ({ value, max, milestones = [], showLabels = false }) => {
  const pct = Math.min(100, max > 0 ? (value / max) * 100 : 0)
  return (
    <div className="w-full">
      <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-emerald-500 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
        {milestones.map((m) => {
          const pos = Math.min(100, (m / max) * 100)
          return (
            <div
              key={m}
              className="absolute top-0 bottom-0 w-0.5 bg-amber-400/80"
              style={{ left: `${pos}%` }}
              title={`Marco ${m}`}
            />
          )
        })}
      </div>
      {showLabels && (
        <div className="flex justify-between mt-1">
          <Text size="xs" color="muted">
            {value.toLocaleString('pt-BR')}
          </Text>
          <Text size="xs" color="muted">
            Meta {max.toLocaleString('pt-BR')}
          </Text>
        </div>
      )}
    </div>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  startIndex: number
  endIndex: number
  onPageChange: (page: number) => void
  onPrevious: () => void
  onNext: () => void
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  onPageChange,
  onPrevious,
  onNext,
}) => {
  if (totalPages <= 1) return null
  return (
    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <Text size="sm" className="text-gray-700 dark:text-gray-300">
          Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} registros
        </Text>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={onPrevious}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Anterior
          </button>
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber: number
              if (totalPages <= 5) pageNumber = i + 1
              else if (currentPage <= 3) pageNumber = i + 1
              else if (currentPage >= totalPages - 2) pageNumber = totalPages - 4 + i
              else pageNumber = currentPage - 2 + i
              return (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === pageNumber
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  )
}

function usePagination<T>(items: T[], page: number, perPage: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / perPage))
  const startIndex = (page - 1) * perPage
  const endIndex = startIndex + perPage
  const paged = items.slice(startIndex, endIndex)
  return { paged, totalPages, startIndex, endIndex }
}

const Campanhas2026: React.FC = () => {
  const { profile } = useAuthContext()
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    'comercial_campanhas2026',
    'view_values'
  )

  const [activeTab, setActiveTab] = useState<ActiveTab>('minimetas2026')
  const [moduleInfo, setModuleInfo] = useState<{ display_name: string; description?: string | null } | null>(null)

  const [codeFilter, setCodeFilter] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [classificationFilter, setClassificationFilter] = useState('')
  const [comercialFilter, setComercialFilter] = useState('')
  const [comercialUsers, setComercialUsers] = useState<ComercialUser[]>([])
  const [loadingComercialUsers, setLoadingComercialUsers] = useState(false)
  const [comercialNameByBrokerId, setComercialNameByBrokerId] = useState<Map<string, string>>(() => new Map())

  const [classificationRefs, setClassificationRefs] = useState<DashCorretorClassificationRef[]>([])

  const [minimetasItems, setMinimetasItems] = useState<DashCorretorMinimetas2026Entry[]>([])
  const [upgradeItems, setUpgradeItems] = useState<DashCorretorClassificationUpgradeEntry[]>([])
  const [campeaoMesItems, setCampeaoMesItems] = useState<DashCorretorCampeaoMesEntry[]>([])
  const [decoleVendasItems, setDecoleVendasItems] = useState<DashCorretorDecoleInfluenciadoresEntry[]>([])
  const [decoleProducaoItems, setDecoleProducaoItems] = useState<DashCorretorDecoleInfluenciadoresEntry[]>([])
  const [reiImediatosItems, setReiImediatosItems] = useState<DashCorretorReiImediatosEntry[]>([])
  const [bateuLevouJunItems, setBateuLevouJunItems] = useState<DashCorretorBateuLevouEntry[]>([])
  const [bateuLevouOutItems, setBateuLevouOutItems] = useState<DashCorretorBateuLevouEntry[]>([])
  const [pasiAppItems, setPasiAppItems] = useState<DashCorretorIndicationsSummaryEntry[]>([])
  const [pasiFarmaItems, setPasiFarmaItems] = useState<DashCorretorIndicationsSummaryEntry[]>([])

  const [loadingMinimetas, setLoadingMinimetas] = useState(false)
  const [loadingUpgrade, setLoadingUpgrade] = useState(false)
  const [loadingCampeaoMes, setLoadingCampeaoMes] = useState(false)
  const [loadingDecoleVendas, setLoadingDecoleVendas] = useState(false)
  const [loadingDecoleProducao, setLoadingDecoleProducao] = useState(false)
  const [loadingReiImediatos, setLoadingReiImediatos] = useState(false)
  const [loadingBateuLevouJun, setLoadingBateuLevouJun] = useState(false)
  const [loadingBateuLevouOut, setLoadingBateuLevouOut] = useState(false)
  const [loadingPasiApp, setLoadingPasiApp] = useState(false)
  const [loadingPasiFarma, setLoadingPasiFarma] = useState(false)

  const [decoleMode, setDecoleMode] = useState<'vendas' | 'producao'>('vendas')
  const [indicacoesSubTab, setIndicacoesSubTab] = useState<IndicacoesSubTab>('pasiSuperApp')
  const [bateuLevouPeriod, setBateuLevouPeriod] = useState<6 | 10>(6)

  const [pageMinimetas, setPageMinimetas] = useState(1)
  const [pageUpgrade, setPageUpgrade] = useState(1)
  const [pageRei, setPageRei] = useState(1)
  const [pageBateuLevou, setPageBateuLevou] = useState(1)
  const [pageDecole, setPageDecole] = useState(1)
  const [pagePasiApp, setPagePasiApp] = useState(1)
  const [pagePasiFarma, setPagePasiFarma] = useState(1)
  const [minimetasSortBy, setMinimetasSortBy] = useState('quantidade')
  const [minimetasSortOrder, setMinimetasSortOrder] = useState<SortOrder>('desc')
  const [upgradeSortBy, setUpgradeSortBy] = useState('gap')
  const [upgradeSortOrder, setUpgradeSortOrder] = useState<SortOrder>('asc')
  const [decoleSortBy, setDecoleSortBy] = useState('posicao')
  const [decoleSortOrder, setDecoleSortOrder] = useState<SortOrder>('asc')
  const [reiSortBy, setReiSortBy] = useState('quantidade')
  const [reiSortOrder, setReiSortOrder] = useState<SortOrder>('desc')
  const [bateuLevouSortBy, setBateuLevouSortBy] = useState('paid_premium')
  const [bateuLevouSortOrder, setBateuLevouSortOrder] = useState<SortOrder>('desc')
  const [pasiAppSortBy, setPasiAppSortBy] = useState('quantidade_ativas')
  const [pasiAppSortOrder, setPasiAppSortOrder] = useState<SortOrder>('desc')
  const [pasiFarmaSortBy, setPasiFarmaSortBy] = useState('quantidade_ativas')
  const [pasiFarmaSortOrder, setPasiFarmaSortOrder] = useState<SortOrder>('desc')
  const [minimetasMarcoFilter, setMinimetasMarcoFilter] = useState<MinimetasMarcoFilter>('all')
  const [upgradeGapFilter, setUpgradeGapFilter] = useState<'all' | (typeof CLASSIFICATION_UPGRADE_GAP_FILTERS)[number]>('all')
  const [reiMarcoFilter, setReiMarcoFilter] = useState<ReiMarcoFilter>('all')
  const [bateuLevouMarcoFilter, setBateuLevouMarcoFilter] = useState<BateuLevouMarcoFilter>('all')
  const [minimetasSubTab, setMinimetasSubTab] = useState<'grafico' | 'detalhamento'>('detalhamento')
  const [selectedBrokerId, setSelectedBrokerId] = useState<string | null>(null)
  const [brokerModalLoading, setBrokerModalLoading] = useState(false)
  const [comercialResponsibleName, setComercialResponsibleName] = useState<string | null>(null)
  const [loadingComercialName, setLoadingComercialName] = useState(false)
  const [brokerPoints, setBrokerPoints] = useState<ShopBroker | null>(null)
  const [loadingBrokerPoints, setLoadingBrokerPoints] = useState(false)
  const [brokerCupons, setBrokerCupons] = useState<ComercialCupom[]>([])
  const [loadingBrokerCupons, setLoadingBrokerCupons] = useState(false)
  const [brokerPasiApp, setBrokerPasiApp] = useState<DashCorretorIndicationsSummaryEntry | null>(null)
  const [brokerPasiFarma, setBrokerPasiFarma] = useState<DashCorretorIndicationsSummaryEntry | null>(null)
  const [loadingPasiIndications, setLoadingPasiIndications] = useState(false)
  const [monthlyPaidPremium, setMonthlyPaidPremium] = useState<number | null>(null)
  const [loadingMonthlyPremium, setLoadingMonthlyPremium] = useState(false)

  const openBrokerDetail = useCallback((brokerId: string) => {
    setSelectedBrokerId(brokerId)
  }, [])

  const closeBrokerDetail = useCallback(() => {
    setSelectedBrokerId(null)
    setBrokerModalLoading(false)
    setComercialResponsibleName(null)
    setLoadingComercialName(false)
    setBrokerPoints(null)
    setLoadingBrokerPoints(false)
    setBrokerCupons([])
    setLoadingBrokerCupons(false)
    setBrokerPasiApp(null)
    setBrokerPasiFarma(null)
    setLoadingPasiIndications(false)
    setMonthlyPaidPremium(null)
    setLoadingMonthlyPremium(false)
  }, [])

  const classificationColorMap = useMemo(
    () => buildClassificationColorMap(classificationRefs),
    [classificationRefs]
  )

  const classificationOptions = useMemo(
    () => classificationRefs.map((c) => c.name).filter(Boolean),
    [classificationRefs]
  )

  const loggedUserName = profile?.name?.trim() ?? ''
  const isSharedFiltersRestrictedByViewValues = loadingViewValues || !canViewValues
  const showDecoleValues = !loadingViewValues && canViewValues
  const sharedFiltersComercialFilter = isSharedFiltersRestrictedByViewValues
    ? (loggedUserName || '\0')
    : comercialFilter

  const displayedComercialFilter = isSharedFiltersRestrictedByViewValues ? loggedUserName : comercialFilter

  const clearSharedFilters = useCallback(() => {
    setCodeFilter('')
    setNameFilter('')
    setClassificationFilter('')
    if (!isSharedFiltersRestrictedByViewValues) {
      setComercialFilter('')
    }
  }, [isSharedFiltersRestrictedByViewValues])

  useEffect(() => {
    void (async () => {
      const result = await modulesService.getByName('comercial_campanhas2026')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Campanhas 2026',
          description: result.data.description,
        })
      }
    })()
    dashcorretorService.listClassificationReferences().then(setClassificationRefs).catch(() => {})
    setLoadingComercialUsers(true)
    void Promise.all([
      comercialGestaoService.listComercialUsers(),
      comercialGestaoService.getBrokerComercialNameMap(),
    ])
      .then(([users, nameMap]) => {
        setComercialUsers(users)
        setComercialNameByBrokerId(nameMap)
      })
      .catch(() => {
        setComercialUsers([])
        setComercialNameByBrokerId(new Map())
      })
      .finally(() => setLoadingComercialUsers(false))
  }, [])

  const loadMinimetas = useCallback(async () => {
    setLoadingMinimetas(true)
    try {
      const data = await dashcorretorService.listMinimetas2026(true)
      setMinimetasItems(data)
    } finally {
      setLoadingMinimetas(false)
    }
  }, [])

  const loadUpgradeCandidates = useCallback(async () => {
    setLoadingUpgrade(true)
    try {
      const data = await dashcorretorService.listClassificationUpgradeCandidates()
      setUpgradeItems(data)
    } finally {
      setLoadingUpgrade(false)
    }
  }, [])

  const loadCampeaoMes = useCallback(async () => {
    setLoadingCampeaoMes(true)
    try {
      // Aba pública: dados completos para todos com permissão show (sem view_values)
      const data = await dashcorretorService.listCampeaoMes(true)
      setCampeaoMesItems(data)
    } finally {
      setLoadingCampeaoMes(false)
    }
  }, [])

  const loadDecoleVendas = useCallback(async () => {
    setLoadingDecoleVendas(true)
    try {
      const data = await dashcorretorService.listDecoleInfluenciadoresVendas(canViewValues)
      setDecoleVendasItems(data)
    } finally {
      setLoadingDecoleVendas(false)
    }
  }, [canViewValues])

  const loadDecoleProducao = useCallback(async () => {
    setLoadingDecoleProducao(true)
    try {
      const data = await dashcorretorService.listDecoleInfluenciadoresProducao(canViewValues)
      setDecoleProducaoItems(data)
    } finally {
      setLoadingDecoleProducao(false)
    }
  }, [canViewValues])

  const loadReiImediatos = useCallback(async () => {
    setLoadingReiImediatos(true)
    try {
      const data = await dashcorretorService.listReiImediatos(true)
      setReiImediatosItems(data)
    } finally {
      setLoadingReiImediatos(false)
    }
  }, [])

  const loadPasiAppIndications = useCallback(async () => {
    setLoadingPasiApp(true)
    try {
      const data = await dashcorretorService.listPasiAppIndications()
      setPasiAppItems(data)
    } finally {
      setLoadingPasiApp(false)
    }
  }, [])

  const loadPasiFarmaIndications = useCallback(async () => {
    setLoadingPasiFarma(true)
    try {
      const data = await dashcorretorService.listPasiFarmaIndications()
      setPasiFarmaItems(data)
    } finally {
      setLoadingPasiFarma(false)
    }
  }, [])

  const loadBateuLevou = useCallback(async () => {
    setLoadingBateuLevouJun(true)
    setLoadingBateuLevouOut(true)
    try {
      const [jun, out] = await Promise.all([
        dashcorretorService.listBateuLevou(6, true),
        dashcorretorService.listBateuLevou(10, true),
      ])
      setBateuLevouJunItems(jun)
      setBateuLevouOutItems(out)
    } finally {
      setLoadingBateuLevouJun(false)
      setLoadingBateuLevouOut(false)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'minimetas2026') void loadMinimetas()
  }, [activeTab, loadMinimetas])

  useEffect(() => {
    if (activeTab === 'upgradeCategoria') void loadUpgradeCandidates()
  }, [activeTab, loadUpgradeCandidates])

  useEffect(() => {
    if (activeTab === 'campeaoMes') void loadCampeaoMes()
  }, [activeTab, loadCampeaoMes])

  useEffect(() => {
    if (activeTab === 'decoleInfluenciadores') {
      void loadDecoleVendas()
      void loadDecoleProducao()
    }
  }, [activeTab, loadDecoleVendas, loadDecoleProducao])

  useEffect(() => {
    if (!showDecoleValues && decoleSortBy === 'value') {
      setDecoleSortBy('posicao')
      setDecoleSortOrder('asc')
    }
  }, [showDecoleValues, decoleSortBy])

  useEffect(() => {
    if (activeTab === 'reiImediatos') void loadReiImediatos()
  }, [activeTab, loadReiImediatos])

  useEffect(() => {
    if (activeTab === 'bateuLevou') void loadBateuLevou()
  }, [activeTab, loadBateuLevou])

  useEffect(() => {
    if (activeTab === 'indicacoes') {
      void loadPasiAppIndications()
      void loadPasiFarmaIndications()
    }
  }, [activeTab, loadPasiAppIndications, loadPasiFarmaIndications])

  useEffect(() => {
    if (!selectedBrokerId) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeBrokerDetail()
    }
    window.addEventListener('keydown', onKeyDown)

    void (async () => {
      setBrokerModalLoading(true)
      setLoadingComercialName(true)
      setLoadingBrokerPoints(true)
      setLoadingBrokerCupons(true)
      setLoadingPasiIndications(true)
      setLoadingMonthlyPremium(true)
      setComercialResponsibleName(null)
      setBrokerPoints(null)
      setBrokerCupons([])
      setBrokerPasiApp(null)
      setBrokerPasiFarma(null)
      setMonthlyPaidPremium(null)
      try {
        const [, comercialName, points, cupons, pasiIndications, premium] = await Promise.all([
          Promise.all([
            loadMinimetas(),
            loadCampeaoMes(),
            loadDecoleVendas(),
            loadDecoleProducao(),
            loadReiImediatos(),
            loadBateuLevou(),
          ]),
          comercialGestaoService.getComercialNameByBrokerId(selectedBrokerId).catch(() => null),
          shopService.getBrokerPoints(selectedBrokerId).catch(() => null),
          comercialService.listCuponsByBrokerId(selectedBrokerId).catch(() => []),
          dashcorretorService.getPasiIndicationsSummaryByBrokerId(selectedBrokerId).catch(() => ({
            app: null,
            farma: null,
          })),
          dashcorretorService.getMonthlyPaidPremium(selectedBrokerId).catch(() => 0),
        ])
        setComercialResponsibleName(comercialName)
        setBrokerPoints(points)
        setBrokerCupons(cupons)
        setBrokerPasiApp(pasiIndications.app)
        setBrokerPasiFarma(pasiIndications.farma)
        setMonthlyPaidPremium(premium)
      } finally {
        setBrokerModalLoading(false)
        setLoadingComercialName(false)
        setLoadingBrokerPoints(false)
        setLoadingBrokerCupons(false)
        setLoadingPasiIndications(false)
        setLoadingMonthlyPremium(false)
      }
    })()

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    selectedBrokerId,
    closeBrokerDetail,
    loadMinimetas,
    loadCampeaoMes,
    loadDecoleVendas,
    loadDecoleProducao,
    loadReiImediatos,
    loadBateuLevou,
  ])

  const brokerDetailData = useMemo(() => {
    if (!selectedBrokerId) return null

    const minimetas = minimetasItems.find((item) => item.broker_id === selectedBrokerId)
    const campeaoMes = campeaoMesItems.filter((item) => item.broker_id === selectedBrokerId)
    const decoleVendas = findDecoleRank(decoleVendasItems, selectedBrokerId)
    const decoleProducao = findDecoleRank(decoleProducaoItems, selectedBrokerId)
    const rei = reiImediatosItems.find((item) => item.broker_id === selectedBrokerId)
    const bateuLevouJun = bateuLevouJunItems.find((item) => item.broker_id === selectedBrokerId)
    const bateuLevouOut = bateuLevouOutItems.find((item) => item.broker_id === selectedBrokerId)

    const pasiApp = pasiAppItems.find((item) => item.broker_id === selectedBrokerId)
    const pasiFarma = pasiFarmaItems.find((item) => item.broker_id === selectedBrokerId)

    const brokerInfo = pickBrokerBase(
      minimetas,
      upgradeItems.find((item) => item.broker_id === selectedBrokerId),
      campeaoMes[0],
      decoleVendas?.item,
      decoleProducao?.item,
      rei,
      bateuLevouJun,
      bateuLevouOut,
      pasiApp,
      pasiFarma
    )

    return {
      brokerInfo,
      minimetas,
      campeaoMes,
      decoleVendas,
      decoleProducao,
      rei,
      bateuLevouJun,
      bateuLevouOut,
    }
  }, [
    selectedBrokerId,
    minimetasItems,
    upgradeItems,
    campeaoMesItems,
    decoleVendasItems,
    decoleProducaoItems,
    reiImediatosItems,
    bateuLevouJunItems,
    bateuLevouOutItems,
    pasiAppItems,
    pasiFarmaItems,
  ])

  useEffect(() => {
    setPageMinimetas(1)
    setPageUpgrade(1)
    setPageRei(1)
    setPageBateuLevou(1)
    setPageDecole(1)
    setPagePasiApp(1)
    setPagePasiFarma(1)
  }, [codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, activeTab, decoleMode, indicacoesSubTab, bateuLevouPeriod, minimetasMarcoFilter, upgradeGapFilter, reiMarcoFilter, bateuLevouMarcoFilter])

  const toggleMinimetasSort = useCallback(
    createToggleSort(minimetasSortBy, setMinimetasSortBy, minimetasSortOrder, setMinimetasSortOrder, () => setPageMinimetas(1)),
    [minimetasSortBy, minimetasSortOrder]
  )
  const toggleUpgradeSort = useCallback(
    createToggleSort(upgradeSortBy, setUpgradeSortBy, upgradeSortOrder, setUpgradeSortOrder, () => setPageUpgrade(1)),
    [upgradeSortBy, upgradeSortOrder]
  )
  const toggleDecoleSort = useCallback(
    createToggleSort(decoleSortBy, setDecoleSortBy, decoleSortOrder, setDecoleSortOrder, () => setPageDecole(1)),
    [decoleSortBy, decoleSortOrder]
  )
  const toggleReiSort = useCallback(
    createToggleSort(reiSortBy, setReiSortBy, reiSortOrder, setReiSortOrder, () => setPageRei(1)),
    [reiSortBy, reiSortOrder]
  )
  const toggleBateuLevouSort = useCallback(
    createToggleSort(bateuLevouSortBy, setBateuLevouSortBy, bateuLevouSortOrder, setBateuLevouSortOrder, () => setPageBateuLevou(1)),
    [bateuLevouSortBy, bateuLevouSortOrder]
  )
  const togglePasiAppSort = useCallback(
    createToggleSort(pasiAppSortBy, setPasiAppSortBy, pasiAppSortOrder, setPasiAppSortOrder, () => setPagePasiApp(1)),
    [pasiAppSortBy, pasiAppSortOrder]
  )
  const togglePasiFarmaSort = useCallback(
    createToggleSort(pasiFarmaSortBy, setPasiFarmaSortBy, pasiFarmaSortOrder, setPasiFarmaSortOrder, () => setPagePasiFarma(1)),
    [pasiFarmaSortBy, pasiFarmaSortOrder]
  )

  const filteredMinimetasBase = useMemo(() => {
    return minimetasItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [minimetasItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const filteredUpgradeBase = useMemo(() => {
    return upgradeItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [upgradeItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const filteredUpgrade = useMemo(() => {
    if (upgradeGapFilter === 'all') return filteredUpgradeBase
    return filteredUpgradeBase.filter((item) => item.gap_to_next_classification <= upgradeGapFilter)
  }, [filteredUpgradeBase, upgradeGapFilter])

  const upgradeKpis = useMemo(() => {
    const byGap: Record<number, number> = {}
    for (const threshold of CLASSIFICATION_UPGRADE_GAP_FILTERS) {
      byGap[threshold] = filteredUpgradeBase.filter((i) => i.gap_to_next_classification <= threshold).length
    }
    return { byGap }
  }, [filteredUpgradeBase])

  const filteredMinimetas = useMemo(() => {
    return filteredMinimetasBase.filter((item) =>
      matchesMinimetasMarcoFilter(item.quantidade, minimetasMarcoFilter)
    )
  }, [filteredMinimetasBase, minimetasMarcoFilter])

  const minimetasKpis = useMemo(() => {
    const counts: Record<number, number> = {}
    MINIMETAS_MILESTONES.forEach((m) => {
      counts[m] = filteredMinimetasBase.filter((i) => i.quantidade >= m).length
    })
    const almostThere = filteredMinimetasBase.filter((i) => {
      const next = getNextMinimetasMilestone(i.quantidade)
      return next !== null && isAlmostAtMinimetasMilestone(i.quantidade, next)
    }).length
    return { counts, almostThere }
  }, [filteredMinimetasBase])

  const minimetasChartData = useMemo(
    () =>
      MINIMETAS_MILESTONES.map((m) => ({
        name: `${m}`,
        corretores: minimetasKpis.counts[m] ?? 0,
      })),
    [minimetasKpis]
  )

  const sortedMinimetas = useMemo(
    () => sortMinimetasItems(filteredMinimetas, minimetasSortBy, minimetasSortOrder),
    [filteredMinimetas, minimetasSortBy, minimetasSortOrder]
  )
  const minimetasPagination = usePagination(sortedMinimetas, pageMinimetas, ITEMS_PER_PAGE)

  const sortedUpgrade = useMemo(
    () => sortUpgradeItems(filteredUpgrade, upgradeSortBy, upgradeSortOrder),
    [filteredUpgrade, upgradeSortBy, upgradeSortOrder]
  )
  const upgradePagination = usePagination(sortedUpgrade, pageUpgrade, ITEMS_PER_PAGE)

  const campeaoMesByMonth = useMemo(() => {
    const map = new Map<string, DashCorretorCampeaoMesEntry>()
    // Aba pública — campeões globais por mês, sem filtro de comercial
    campeaoMesItems
      .filter((item) => normalizeReferenceMonthKey(item.reference_month) >= CAMPEAO_MES_START)
      .forEach((item) => {
        const key = normalizeReferenceMonthKey(item.reference_month)
        const existing = map.get(key)
        if (!existing || item.value > existing.value) {
          map.set(key, item)
        }
      })
    return map
  }, [campeaoMesItems])

  const campeaoMesMonths2026 = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => {
      const monthNum = i + 3
      const month = String(monthNum).padStart(2, '0')
      const key = `2026-${month}`
      return { key, label: MONTH_LABELS[monthNum - 1], champion: campeaoMesByMonth.get(key) ?? null }
    })
  }, [campeaoMesByMonth])

  const decoleItems = decoleMode === 'vendas' ? decoleVendasItems : decoleProducaoItems
  const loadingDecole = decoleMode === 'vendas' ? loadingDecoleVendas : loadingDecoleProducao

  // Aba pública — ranking completo, sem filtros compartilhados nem view_values
  const filteredDecole = useMemo(() => decoleItems, [decoleItems])

  const decoleRankById = useMemo(() => {
    const map = new Map<string, number>()
    filteredDecole.forEach((item, index) => map.set(item.id, index + 1))
    return map
  }, [filteredDecole])

  const sortedDecole = useMemo(
    () => sortDecoleItems(filteredDecole, decoleSortBy, decoleSortOrder, decoleRankById),
    [filteredDecole, decoleSortBy, decoleSortOrder, decoleRankById]
  )
  const decolePagination = usePagination(sortedDecole, pageDecole, ITEMS_PER_PAGE)
  const decoleTableColSpan = showDecoleValues ? 6 : 5

  const filteredReiBase = useMemo(() => {
    return reiImediatosItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [reiImediatosItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const filteredRei = useMemo(() => {
    return filteredReiBase.filter((item) =>
      matchesReiMarcoFilter(item.quantidade, reiMarcoFilter)
    )
  }, [filteredReiBase, reiMarcoFilter])

  const reiKpis = useMemo(() => {
    const counts: Record<number, number> = {}
    REI_IMEDIATOS_MILESTONES.forEach((m) => {
      counts[m] = filteredReiBase.filter((i) => i.quantidade >= m).length
    })
    const almostThere = filteredReiBase.filter((i) => {
      const next = getNextReiMilestone(i.quantidade)
      return next !== null && isAlmostAtReiMilestone(i.quantidade, next)
    }).length
    return { counts, almostThere }
  }, [filteredReiBase])

  const sortedRei = useMemo(
    () => sortReiItems(filteredRei, reiSortBy, reiSortOrder),
    [filteredRei, reiSortBy, reiSortOrder]
  )
  const reiPagination = usePagination(sortedRei, pageRei, ITEMS_PER_PAGE)

  const bateuLevouItems = bateuLevouPeriod === 6 ? bateuLevouJunItems : bateuLevouOutItems
  const loadingBateuLevou = bateuLevouPeriod === 6 ? loadingBateuLevouJun : loadingBateuLevouOut

  const filteredBateuLevouBase = useMemo(() => {
    return bateuLevouItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [bateuLevouItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const filteredBateuLevou = useMemo(() => {
    return filteredBateuLevouBase.filter((item) =>
      matchesBateuLevouMarcoFilter(item.paid_premium, bateuLevouMarcoFilter)
    )
  }, [filteredBateuLevouBase, bateuLevouMarcoFilter])

  const bateuLevouKpis = useMemo(() => {
    const bateu = filteredBateuLevouBase.filter((i) => i.paid_premium >= BATEU_LEVOU_GOAL).length
    const quaseLa = filteredBateuLevouBase.filter((i) => isAlmostAtBateuLevouGoal(i.paid_premium)).length
    return { bateu, quaseLa }
  }, [filteredBateuLevouBase])

  const sortedBateuLevou = useMemo(
    () => sortBateuLevouItems(filteredBateuLevou, bateuLevouSortBy, bateuLevouSortOrder),
    [filteredBateuLevou, bateuLevouSortBy, bateuLevouSortOrder]
  )
  const bateuLevouPagination = usePagination(sortedBateuLevou, pageBateuLevou, ITEMS_PER_PAGE)

  const filteredPasiApp = useMemo(() => {
    return pasiAppItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [pasiAppItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const filteredPasiFarma = useMemo(() => {
    return pasiFarmaItems.filter((item) =>
      campaignRowMatchesFilters(item, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId)
    )
  }, [pasiFarmaItems, codeFilter, nameFilter, classificationFilter, sharedFiltersComercialFilter, comercialNameByBrokerId])

  const sortedPasiApp = useMemo(
    () => sortPasiIndicationsItems(filteredPasiApp, pasiAppSortBy, pasiAppSortOrder),
    [filteredPasiApp, pasiAppSortBy, pasiAppSortOrder]
  )
  const pasiAppPagination = usePagination(sortedPasiApp, pagePasiApp, ITEMS_PER_PAGE)

  const sortedPasiFarma = useMemo(
    () => sortPasiIndicationsItems(filteredPasiFarma, pasiFarmaSortBy, pasiFarmaSortOrder),
    [filteredPasiFarma, pasiFarmaSortBy, pasiFarmaSortOrder]
  )
  const pasiFarmaPagination = usePagination(sortedPasiFarma, pagePasiFarma, ITEMS_PER_PAGE)

  const isIndicacoesSuperApp = indicacoesSubTab === 'pasiSuperApp'
  const filteredIndicacoes = isIndicacoesSuperApp ? filteredPasiApp : filteredPasiFarma
  const loadingIndicacoes = isIndicacoesSuperApp ? loadingPasiApp : loadingPasiFarma
  const indicacoesSortBy = isIndicacoesSuperApp ? pasiAppSortBy : pasiFarmaSortBy
  const indicacoesSortOrder = isIndicacoesSuperApp ? pasiAppSortOrder : pasiFarmaSortOrder
  const toggleIndicacoesSort = isIndicacoesSuperApp ? togglePasiAppSort : togglePasiFarmaSort
  const indicacoesPagination = isIndicacoesSuperApp ? pasiAppPagination : pasiFarmaPagination
  const indicacoesPage = isIndicacoesSuperApp ? pagePasiApp : pagePasiFarma
  const setIndicacoesPage = isIndicacoesSuperApp ? setPagePasiApp : setPagePasiFarma

  const tabConfig: { id: ActiveTab; label: React.ReactNode; icon: React.ReactNode }[] = [
    { id: 'minimetas2026', label: 'Minimetas', icon: <StarIcon className="h-4 w-4" /> },
    { id: 'campeaoMes', label: 'Campeão Mês', icon: <TrophyIcon className="h-4 w-4" /> },
    { id: 'decoleInfluenciadores', label: 'Decole c/ Influenc.', icon: <RocketLaunchIcon className="h-4 w-4" /> },
    { id: 'reiImediatos', label: 'Bateu, Levou! Imediatos', icon: <BoltIcon className="h-4 w-4" /> },
    { id: 'bateuLevou', label: 'Bateu Levou', icon: <FireIcon className="h-4 w-4" /> },
    { id: 'indicacoes', label: 'Indicações', icon: <UserPlusIcon className="h-4 w-4" /> },
    { id: 'upgradeCategoria', label: 'Próximo de Upgrade', icon: <ArrowTrendingUpIcon className="h-4 w-4" /> },
  ]

  const getDecoleRowClass = (position: number): string => {
    if (position <= 3) return 'bg-amber-50 dark:bg-amber-900/20'
    if (position <= 10) return 'bg-emerald-50 dark:bg-emerald-900/15'
    if (position <= 20) return 'bg-yellow-50 dark:bg-yellow-900/10'
    return 'opacity-70'
  }

  const getDecolePositionLabel = (position: number): React.ReactNode => {
    if (position === 1) return <span className="font-bold text-amber-600">🥇 1°</span>
    if (position === 2) return <span className="font-bold text-gray-500">🥈 2°</span>
    if (position === 3) return <span className="font-bold text-amber-800">🥉 3°</span>
    if (position <= 20) return <span className="font-medium">{position}°</span>
    return <span className="text-gray-400 italic">Após Top 20</span>
  }

  const campaignSharedFilters = (
    <CampaignSharedFilters
      embedded
      codeFilter={codeFilter}
      nameFilter={nameFilter}
      classificationFilter={classificationFilter}
      comercialFilter={displayedComercialFilter}
      classificationOptions={classificationOptions}
      comercialUsers={comercialUsers}
      loadingComercialUsers={loadingComercialUsers}
      comercialFilterLocked={isSharedFiltersRestrictedByViewValues}
      onCodeChange={setCodeFilter}
      onNameChange={setNameFilter}
      onClassificationChange={setClassificationFilter}
      onComercialChange={setComercialFilter}
      onClear={clearSharedFilters}
    />
  )

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="comercial_campanhas2026" permissionType="show">
          <div>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name ?? 'Campanhas 2026'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
              <Text size="sm" color="muted" className="mt-2">
                Visão estratégica do desempenho dos corretores nas campanhas - identifique quem está próximo das metas e quem já bateu.
              </Text>
            </div>

            <div className="px-6 pt-4">
              <ClassificationKpiCards standalone />
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex flex-wrap gap-x-4 gap-y-2">
                {tabConfig.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                      activeTab === tab.id
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="px-6 py-6">
              {/* MINIMETAS */}
              {activeTab === 'minimetas2026' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Minimetas 2026
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Marcos de premiação: 10, 20, 30, 50 e 100 propostas
                    </Text>
                  </div>

                  {campaignSharedFilters}

                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-6">
                      <button
                        type="button"
                        onClick={() => setMinimetasSubTab('detalhamento')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          minimetasSubTab === 'detalhamento'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Detalhamento
                      </button>
                      <button
                        type="button"
                        onClick={() => setMinimetasSubTab('grafico')}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          minimetasSubTab === 'grafico'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Gráfico
                      </button>
                    </nav>
                  </div>

                  {minimetasSubTab === 'grafico' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <Text size="lg" className="text-gray-900 dark:text-white mb-4">
                        Corretores por marco atingido
                      </Text>
                      {loadingMinimetas ? (
                        <div className="flex items-center justify-center py-16">
                          <LoadingSpinner />
                        </div>
                      ) : !minimetasChartData.some((d) => d.corretores > 0) ? (
                        <div className="flex items-center justify-center py-16">
                          <Text size="sm" color="muted">Nenhum dado disponível para o gráfico.</Text>
                        </div>
                      ) : (
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={minimetasChartData}>
                              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                              <XAxis dataKey="name" tickFormatter={(v) => `${v} prop.`} />
                              <YAxis allowDecimals={false} />
                              <Tooltip formatter={(value) => [value, 'Corretores']} labelFormatter={(l) => `Marco ${l} propostas`} />
                              <Bar dataKey="corretores" radius={[4, 4, 0, 0]}>
                                {minimetasChartData.map((_, i) => (
                                  <Cell key={i} fill={MINIMETAS_CHART_COLORS[i] ?? '#008B95'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}

                  {minimetasSubTab === 'detalhamento' && (
                    <>
                  <TabFiltersPanel
                    description="Selecione um card para filtrar a lista - clique novamente para remover."
                    showClear={minimetasMarcoFilter !== 'all'}
                    onClear={() => {
                      setMinimetasMarcoFilter('all')
                      setPageMinimetas(1)
                    }}
                  >
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {MINIMETAS_MILESTONES.map((m) => (
                      <KpiCard
                        key={m}
                        compact
                        filterable
                        label={`Bateram ${m} propostas`}
                        value={minimetasKpis.counts[m] ?? 0}
                        gradient={MINIMETAS_MILESTONE_GRADIENTS[m]}
                        isActive={minimetasMarcoFilter === m}
                        onClick={() => {
                          setMinimetasMarcoFilter((prev) => (prev === m ? 'all' : m))
                          setPageMinimetas(1)
                        }}
                      />
                    ))}
                    <KpiCard
                      compact
                      filterable
                      label="Quase lá (≤5 propostas)"
                      value={minimetasKpis.almostThere}
                      gradient="from-amber-400 via-orange-500 to-red-500"
                      icon={<SparklesIcon className="h-5 w-5" />}
                      isActive={minimetasMarcoFilter === 'quase_la'}
                      onClick={() => {
                        setMinimetasMarcoFilter((prev) => (prev === 'quase_la' ? 'all' : 'quase_la'))
                        setPageMinimetas(1)
                      }}
                    />
                  </div>
                  </TabFiltersPanel>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingMinimetas ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    ) : filteredMinimetas.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum registro encontrado.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="broker_code" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                <SortableTableHeader sortKey="quantidade" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Propostas</SortableTableHeader>
                                <SortableTableHeader sortKey="marcos" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Marcos</SortableTableHeader>
                                <SortableTableHeader sortKey="proxima_meta" currentSort={minimetasSortBy} sortOrder={minimetasSortOrder} onSort={toggleMinimetasSort} compact className="px-4 py-3">Próxima meta</SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {minimetasPagination.paged.map((item) => {
                                const achieved = getAchievedMinimetasMilestones(item.quantidade)
                                const next = getNextMinimetasMilestone(item.quantidade)
                                const almost = next !== null && isAlmostAtMinimetasMilestone(item.quantidade, next)
                                return (
                                  <tr
                                    key={item.broker_id}
                                    {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                      achieved.includes(100)
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                        : almost
                                          ? 'bg-amber-50 dark:bg-amber-900/15 ring-1 ring-amber-300 dark:ring-amber-700'
                                          : ''
                                    }`}
                                  >
                                    <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                    <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                    <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold">{item.quantidade.toLocaleString('pt-BR')}</td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-wrap gap-1">
                                        {MINIMETAS_MILESTONES.map((m) => (
                                          <span
                                            key={m}
                                            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                              achieved.includes(m)
                                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                            }`}
                                          >
                                            {m}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {next ? (
                                        <span className={almost ? 'text-amber-600 font-semibold flex items-center gap-1' : ''}>
                                          {almost && <SparklesIcon className="h-4 w-4" />}
                                          Faltam {next - item.quantidade} para {next}
                                        </span>
                                      ) : (
                                        <span className="text-emerald-600 font-semibold">Todas as metas ✓</span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={pageMinimetas}
                          totalPages={minimetasPagination.totalPages}
                          totalItems={filteredMinimetas.length}
                          startIndex={minimetasPagination.startIndex}
                          endIndex={minimetasPagination.endIndex}
                          onPageChange={setPageMinimetas}
                          onPrevious={() => setPageMinimetas((p) => Math.max(1, p - 1))}
                          onNext={() => setPageMinimetas((p) => Math.min(minimetasPagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                    </>
                  )}
                </div>
              )}

              {/* PRÓXIMOS DE UPGRADE */}
              {activeTab === 'upgradeCategoria' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Próximos de upgrade
                    </Heading>
                  </div>

                  {campaignSharedFilters}

                  <TabFiltersPanel
                    description="Selecione um card para filtrar pelo valor que falta - clique novamente para remover."
                    showClear={upgradeGapFilter !== 'all'}
                    onClear={() => {
                      setUpgradeGapFilter('all')
                      setPageUpgrade(1)
                    }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {CLASSIFICATION_UPGRADE_GAP_FILTERS.map((threshold) => (
                        <KpiCard
                          key={threshold}
                          compact
                          filterable
                          label={`Faltam menos de ${formatCurrencyBRL(threshold)} da próxima categoria`}
                          value={upgradeKpis.byGap[threshold] ?? 0}
                          gradient="from-emerald-400 via-teal-500 to-cyan-600"
                          isActive={upgradeGapFilter === threshold}
                          onClick={() => {
                            setUpgradeGapFilter((prev) => (prev === threshold ? 'all' : threshold))
                            setPageUpgrade(1)
                          }}
                        />
                      ))}
                    </div>
                  </TabFiltersPanel>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingUpgrade ? (
                      <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    ) : filteredUpgrade.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum corretor próximo de upgrade.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="broker_code" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                <SortableTableHeader sortKey="next_classification" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">Próxima</SortableTableHeader>
                                <SortableTableHeader sortKey="gap" currentSort={upgradeSortBy} sortOrder={upgradeSortOrder} onSort={toggleUpgradeSort} compact className="px-4 py-3">Falta</SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {upgradePagination.paged.map((item) => (
                                <tr
                                  key={item.broker_id}
                                  {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                    item.gap_to_next_classification <= CLASSIFICATION_UPGRADE_NEAR_GAP
                                      ? 'bg-amber-50 dark:bg-amber-900/15 ring-1 ring-amber-300 dark:ring-amber-700'
                                      : ''
                                  }`}
                                >
                                  <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                  <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                  <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                  </td>
                                  <td className="px-4 py-3 text-sm uppercase">
                                    <ClassificationLabel classification={item.next_classification_name} colorMap={classificationColorMap} />
                                  </td>
                                  <td className="px-4 py-3 text-sm text-amber-700 dark:text-amber-300 font-semibold">
                                    {formatCurrencyBRL(item.gap_to_next_classification)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={pageUpgrade}
                          totalPages={upgradePagination.totalPages}
                          totalItems={filteredUpgrade.length}
                          startIndex={upgradePagination.startIndex}
                          endIndex={upgradePagination.endIndex}
                          onPageChange={setPageUpgrade}
                          onPrevious={() => setPageUpgrade((p) => Math.max(1, p - 1))}
                          onNext={() => setPageUpgrade((p) => Math.min(upgradePagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* CAMPEÃO DO MÊS */}
              {activeTab === 'campeaoMes' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Campeão do Mês
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Campeões a partir de março/2026
                    </Text>
                  </div>

                  {loadingCampeaoMes ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {campeaoMesMonths2026.map(({ key, label, champion }) => (
                        <div
                          key={key}
                          {...(champion ? getBrokerCardHandlers(champion.broker_id, openBrokerDetail) : {})}
                          className={`rounded-xl p-4 border shadow-sm min-h-[180px] flex flex-col ${
                            champion
                              ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-200 dark:from-amber-900/30 dark:to-yellow-900/20 dark:border-amber-700 cursor-pointer hover:shadow-md transition-shadow'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200/60 dark:border-amber-800/40">
                            <TrophyIcon className={`h-5 w-5 shrink-0 ${champion ? 'text-amber-600' : 'text-gray-300'}`} />
                            <Text size="sm" className="font-bold text-gray-900 dark:text-white">
                              {label}/2026
                            </Text>
                          </div>
                          {champion ? (
                            <div className="space-y-2 flex-1">
                              <Text size="sm" className="font-semibold text-gray-900 dark:text-white leading-snug" title={champion.broker_name}>
                                {champion.broker_name}
                              </Text>
                              <div className="space-y-1">
                                <Text size="xs" color="muted">
                                  Código: <span className="font-medium text-gray-700 dark:text-gray-300">{champion.broker_code ?? '—'}</span>
                                </Text>
                                <Text size="xs" color="muted">
                                  UF: <span className="font-medium text-gray-700 dark:text-gray-300">{champion.state?.trim() || '—'}</span>
                                </Text>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Text size="xs" color="muted">Categoria:</Text>
                                  <ClassificationLabel classification={champion.classification} colorMap={classificationColorMap} />
                                </div>
                                {champion.value > 0 && (
                                  <Text size="xs" className="text-amber-800 dark:text-amber-300 font-semibold pt-1">
                                    Valor: {formatCurrency(champion.value)}
                                  </Text>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex-1 flex items-center justify-center">
                              <ClockIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" aria-label="A definir" title="A definir" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* DECOLE COM INFLUENCIADORES */}
              {activeTab === 'decoleInfluenciadores' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Decole com Influenciadores
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Ranking até a 20° colocação - após isso, posição indicada como &quot;Após Top 20&quot;
                    </Text>
                  </div>

                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-6">
                      <button
                        type="button"
                        onClick={() => { setDecoleMode('vendas'); setPageDecole(1) }}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          decoleMode === 'vendas'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Vendas
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDecoleMode('producao'); setPageDecole(1) }}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          decoleMode === 'producao'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Produção
                      </button>
                    </nav>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingDecole ? (
                      <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
                    ) : filteredDecole.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum registro encontrado.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="posicao" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">Posição</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_code" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                {showDecoleValues && (
                                  <SortableTableHeader sortKey="value" currentSort={decoleSortBy} sortOrder={decoleSortOrder} onSort={toggleDecoleSort} compact className="px-4 py-3">Valor</SortableTableHeader>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {decolePagination.paged.map((item, idx) => {
                                const position = decoleRankById.get(item.id) ?? decolePagination.startIndex + idx + 1
                                const prevRank =
                                  idx > 0
                                    ? decoleRankById.get(decolePagination.paged[idx - 1].id) ?? 0
                                    : 0
                                const showSeparator = position > 20 && (idx === 0 || prevRank <= 20)
                                return (
                                  <React.Fragment key={item.id}>
                                    {showSeparator && (
                                      <tr className="bg-gray-100 dark:bg-gray-700/50">
                                        <td colSpan={decoleTableColSpan} className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                          - Além do Top 20 -
                                        </td>
                                      </tr>
                                    )}
                                    <tr
                                      {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${getDecoleRowClass(position)}`}
                                    >
                                      <td className="px-4 py-3 text-sm">{getDecolePositionLabel(position)}</td>
                                      <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                      <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                      <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                      <td className="px-4 py-3 text-sm">
                                        <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                      </td>
                                      {showDecoleValues && (
                                        <td className="px-4 py-3 text-sm text-right font-medium">
                                          {formatCurrency(item.value)}
                                        </td>
                                      )}
                                    </tr>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={pageDecole}
                          totalPages={decolePagination.totalPages}
                          totalItems={filteredDecole.length}
                          startIndex={decolePagination.startIndex}
                          endIndex={decolePagination.endIndex}
                          onPageChange={setPageDecole}
                          onPrevious={() => setPageDecole((p) => Math.max(1, p - 1))}
                          onNext={() => setPageDecole((p) => Math.min(decolePagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* REI DOS IMEDIATOS */}
              {activeTab === 'reiImediatos' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Bateu, Levou! Imediatos
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Premiação nos marcos de 500 e 1.000 imediatos
                    </Text>
                  </div>

                  {campaignSharedFilters}

                  <TabFiltersPanel
                    description="Selecione um card para filtrar a lista - clique novamente para remover."
                    showClear={reiMarcoFilter !== 'all'}
                    onClear={() => {
                      setReiMarcoFilter('all')
                      setPageRei(1)
                    }}
                  >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {REI_IMEDIATOS_MILESTONES.map((m) => (
                      <KpiCard
                        key={m}
                        compact
                        filterable
                        label={`Bateram ${m.toLocaleString('pt-BR')} imediatos`}
                        value={reiKpis.counts[m] ?? 0}
                        gradient={REI_IMEDIATOS_MILESTONE_GRADIENTS[m]}
                        isActive={reiMarcoFilter === m}
                        onClick={() => {
                          setReiMarcoFilter((prev) => (prev === m ? 'all' : m))
                          setPageRei(1)
                        }}
                      />
                    ))}
                    <KpiCard
                      compact
                      filterable
                      label={`Quase lá (≤${REI_QUASE_LA_MARGIN} faltando)`}
                      value={reiKpis.almostThere}
                      gradient="from-amber-400 via-orange-500 to-red-500"
                      icon={<SparklesIcon className="h-5 w-5" />}
                      isActive={reiMarcoFilter === 'quase_la'}
                      onClick={() => {
                        setReiMarcoFilter((prev) => (prev === 'quase_la' ? 'all' : 'quase_la'))
                        setPageRei(1)
                      }}
                    />
                  </div>
                  </TabFiltersPanel>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingReiImediatos ? (
                      <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
                    ) : filteredRei.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum registro encontrado.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="broker_code" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                <SortableTableHeader sortKey="quantidade" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Imediatos</SortableTableHeader>
                                <SortableTableHeader sortKey="progresso" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3 min-w-[180px]">Progresso</SortableTableHeader>
                                <SortableTableHeader sortKey="premiacao" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Premiação</SortableTableHeader>
                                <SortableTableHeader sortKey="proximo_marco" currentSort={reiSortBy} sortOrder={reiSortOrder} onSort={toggleReiSort} compact className="px-4 py-3">Próximo marco</SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {reiPagination.paged.map((item) => {
                                const achieved = getAchievedReiMilestones(item.quantidade)
                                const next = getNextReiMilestone(item.quantidade)
                                const almost = next !== null && isAlmostAtReiMilestone(item.quantidade, next)
                                return (
                                  <tr
                                    key={item.broker_id}
                                    {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                      achieved.includes(1000)
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                        : almost
                                          ? 'bg-amber-50 dark:bg-amber-900/15'
                                          : ''
                                    }`}
                                  >
                                    <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                    <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                    <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold">{item.quantidade.toLocaleString('pt-BR')}</td>
                                    <td className="px-4 py-3">
                                      <ProgressBar value={item.quantidade} max={next ?? 1000} milestones={[...REI_IMEDIATOS_MILESTONES]} />
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex gap-1">
                                        {REI_IMEDIATOS_MILESTONES.map((m) => (
                                          <span
                                            key={m}
                                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                                              achieved.includes(m)
                                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                                            }`}
                                          >
                                            {m} {achieved.includes(m) ? '✓' : ''}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {next ? (
                                        <span className={almost ? 'text-amber-600 font-semibold' : ''}>
                                          Faltam {next - item.quantidade} para {next}
                                        </span>
                                      ) : (
                                        <span className="text-emerald-600 font-semibold">Meta máxima ✓</span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={pageRei}
                          totalPages={reiPagination.totalPages}
                          totalItems={filteredRei.length}
                          startIndex={reiPagination.startIndex}
                          endIndex={reiPagination.endIndex}
                          onPageChange={setPageRei}
                          onPrevious={() => setPageRei((p) => Math.max(1, p - 1))}
                          onNext={() => setPageRei((p) => Math.min(reiPagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* BATEU LEVOU */}
              {activeTab === 'bateuLevou' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Bateu Levou
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Premiação ao atingir R$ 3.000 em prêmio pago
                    </Text>
                  </div>

                  {campaignSharedFilters}

                  <TabFiltersPanel
                    description="Selecione o período e um marco para filtrar a lista - clique novamente no card para remover."
                    showClear={bateuLevouMarcoFilter !== 'all'}
                    onClear={() => {
                      setBateuLevouMarcoFilter('all')
                      setPageBateuLevou(1)
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div>
                        <Text size="sm" className={FILTER_FIELD_LABEL_CLASS}>
                          Período
                        </Text>
                        <Select
                          value={String(bateuLevouPeriod)}
                          onChange={(e) => {
                            setBateuLevouPeriod(Number(e.target.value) as 6 | 10)
                            setBateuLevouMarcoFilter('all')
                            setPageBateuLevou(1)
                          }}
                          className="w-full"
                        >
                          <option value="6">Período Junho</option>
                          <option value="10">Período Outubro</option>
                        </Select>
                      </div>
                    </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <KpiCard
                      compact
                      filterable
                      label="Bateram R$ 3.000"
                      value={bateuLevouKpis.bateu}
                      gradient="from-emerald-500 to-emerald-600"
                      icon={<TrophyIcon className="h-5 w-5" />}
                      isActive={bateuLevouMarcoFilter === 'meta'}
                      onClick={() => {
                        setBateuLevouMarcoFilter((prev) => (prev === 'meta' ? 'all' : 'meta'))
                        setPageBateuLevou(1)
                      }}
                    />
                    <KpiCard
                      compact
                      filterable
                      label={`Quase lá (≥ ${formatCurrency(BATEU_LEVOU_GOAL - BATEU_LEVOU_QUASE_LA_MARGIN)})`}
                      value={bateuLevouKpis.quaseLa}
                      gradient="from-amber-400 via-orange-500 to-red-500"
                      icon={<SparklesIcon className="h-5 w-5" />}
                      isActive={bateuLevouMarcoFilter === 'quase_la'}
                      onClick={() => {
                        setBateuLevouMarcoFilter((prev) => (prev === 'quase_la' ? 'all' : 'quase_la'))
                        setPageBateuLevou(1)
                      }}
                    />
                  </div>
                  </TabFiltersPanel>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingBateuLevou ? (
                      <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
                    ) : filteredBateuLevou.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum registro encontrado.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="broker_code" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                <SortableTableHeader sortKey="progresso" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3 min-w-[160px]">Progresso (R$ 3.000)</SortableTableHeader>
                                <SortableTableHeader sortKey="paid_premium" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Prêmio pago</SortableTableHeader>
                                <SortableTableHeader sortKey="pending_premium" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Pendente</SortableTableHeader>
                                <SortableTableHeader sortKey="total_projetado" currentSort={bateuLevouSortBy} sortOrder={bateuLevouSortOrder} onSort={toggleBateuLevouSort} compact className="px-4 py-3">Total projetado</SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {bateuLevouPagination.paged.map((item) => {
                                const bateu = item.paid_premium >= BATEU_LEVOU_GOAL
                                const quase = isAlmostAtBateuLevouGoal(item.paid_premium)
                                const totalProjetado = item.paid_premium + item.pending_premium
                                return (
                                  <tr
                                    key={item.broker_id}
                                    {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                                      bateu
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20'
                                        : quase
                                          ? 'bg-amber-50 dark:bg-amber-900/15'
                                          : ''
                                    }`}
                                  >
                                    <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                    <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                    <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                    </td>
                                    <td className="px-4 py-3">
                                      <ProgressBar value={item.paid_premium} max={BATEU_LEVOU_GOAL} milestones={[BATEU_LEVOU_GOAL]} showLabels />
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(item.paid_premium)}</td>
                                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.pending_premium)}</td>
                                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(totalProjetado)}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={pageBateuLevou}
                          totalPages={bateuLevouPagination.totalPages}
                          totalItems={filteredBateuLevou.length}
                          startIndex={bateuLevouPagination.startIndex}
                          endIndex={bateuLevouPagination.endIndex}
                          onPageChange={setPageBateuLevou}
                          onPrevious={() => setPageBateuLevou((p) => Math.max(1, p - 1))}
                          onNext={() => setPageBateuLevou((p) => Math.min(bateuLevouPagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* INDICAÇÕES */}
              {activeTab === 'indicacoes' && (
                <div className="space-y-6">
                  <div>
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Indicações
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Total de indicações ativas por corretor
                    </Text>
                  </div>

                  {campaignSharedFilters}

                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-6">
                      <button
                        type="button"
                        onClick={() => { setIndicacoesSubTab('pasiSuperApp'); setPagePasiApp(1) }}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          indicacoesSubTab === 'pasiSuperApp'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        PASI Super APP
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIndicacoesSubTab('pasiFarma'); setPagePasiFarma(1) }}
                        className={`py-3 px-1 border-b-2 font-medium text-sm ${
                          indicacoesSubTab === 'pasiFarma'
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        PASI Farma
                      </button>
                    </nav>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {loadingIndicacoes ? (
                      <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
                    ) : filteredIndicacoes.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <Text size="sm" color="muted">Nenhum registro encontrado.</Text>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <SortableTableHeader sortKey="broker_code" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Código</SortableTableHeader>
                                <SortableTableHeader sortKey="broker_name" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Corretor</SortableTableHeader>
                                <SortableTableHeader sortKey="state" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">UF</SortableTableHeader>
                                <SortableTableHeader sortKey="classification" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Categoria</SortableTableHeader>
                                <SortableTableHeader sortKey="quantidade_ativas" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Ativas</SortableTableHeader>
                                <SortableTableHeader sortKey="quantidade_inativas" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Inativas</SortableTableHeader>
                                <SortableTableHeader sortKey="quantidade_total" currentSort={indicacoesSortBy} sortOrder={indicacoesSortOrder} onSort={toggleIndicacoesSort} compact className="px-4 py-3">Total</SortableTableHeader>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {indicacoesPagination.paged.map((item) => (
                                <tr
                                  key={item.broker_id}
                                  {...getBrokerRowHandlers(item.broker_id, openBrokerDetail)}
                                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                  <td className="px-4 py-3 text-sm">{item.broker_code ?? '—'}</td>
                                  <td className="px-4 py-3 text-sm" title={item.broker_name}>{truncateName(item.broker_name)}</td>
                                  <td className="px-4 py-3 text-sm">{item.state?.trim() || '—'}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <ClassificationLabel classification={item.classification} colorMap={classificationColorMap} />
                                  </td>
                                  <td className="px-4 py-3 text-sm font-semibold">{item.quantidade_ativas.toLocaleString('pt-BR')}</td>
                                  <td className="px-4 py-3 text-sm">{item.quantidade_inativas.toLocaleString('pt-BR')}</td>
                                  <td className="px-4 py-3 text-sm">{item.quantidade_total.toLocaleString('pt-BR')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Pagination
                          currentPage={indicacoesPage}
                          totalPages={indicacoesPagination.totalPages}
                          totalItems={filteredIndicacoes.length}
                          startIndex={indicacoesPagination.startIndex}
                          endIndex={indicacoesPagination.endIndex}
                          onPageChange={setIndicacoesPage}
                          onPrevious={() => setIndicacoesPage((p) => Math.max(1, p - 1))}
                          onNext={() => setIndicacoesPage((p) => Math.min(indicacoesPagination.totalPages, p + 1))}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {selectedBrokerId && brokerDetailData && (
              <BrokerDetailModal
                loading={brokerModalLoading}
                classificationColorMap={classificationColorMap}
                classificationRefs={classificationRefs}
                monthlyPaidPremium={monthlyPaidPremium}
                loadingMonthlyPremium={loadingMonthlyPremium}
                onClose={closeBrokerDetail}
                brokerInfo={brokerDetailData.brokerInfo}
                comercialResponsibleName={comercialResponsibleName}
                loadingComercialName={loadingComercialName}
                brokerPoints={brokerPoints}
                loadingBrokerPoints={loadingBrokerPoints}
                brokerCupons={brokerCupons}
                loadingBrokerCupons={loadingBrokerCupons}
                minimetas={brokerDetailData.minimetas}
                campeaoMes={brokerDetailData.campeaoMes}
                decoleVendas={brokerDetailData.decoleVendas}
                decoleProducao={brokerDetailData.decoleProducao}
                rei={brokerDetailData.rei}
                bateuLevouJun={brokerDetailData.bateuLevouJun}
                bateuLevouOut={brokerDetailData.bateuLevouOut}
                pasiApp={brokerPasiApp}
                pasiFarma={brokerPasiFarma}
                loadingPasiIndications={loadingPasiIndications}
              />
            )}
          </div>
        </PermissionGuard>
    </div>
  )
}

export default Campanhas2026
